import React, { useCallback, useEffect, useState } from 'react';

import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogoImg from '../images/phyfarmlogo-medium-english 1.png';
import { LuCopy } from "react-icons/lu";
import { LuUserPlus } from "react-icons/lu";
import { FaWhatsapp } from "react-icons/fa";
import { Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { baseURL, MANAGE_BASE_URL } from '../config';
import { viewPermission } from '../utils/helpers';
import { useNavigate, useLocation } from 'react-router-dom';
import { RiLogoutCircleLine } from 'react-icons/ri';
import { BsQrCode } from "react-icons/bs";
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { IoCloseOutline } from "react-icons/io5";

import { checkAuth, logout } from '../apis/auth/api';
import { moduleNavGroups } from '../features/manifests';
import Typography from '@mui/material/Typography';

const drawerWidth = 270;

/* ─── Mixins ─────────────────────────────────────────────────────────── */
const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    // Mobile: the open drawer overlays content (paper is position:fixed) instead
    // of squeezing a 390px viewport; the root stops reserving layout width.
    [theme.breakpoints.down('md')]: {
        boxShadow: '8px 0 32px rgba(0,0,0,0.45)',
    },
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
    // Mobile: no mini-rail — closed means hidden; the AppBar hamburger reopens it.
    [theme.breakpoints.down('md')]: {
        width: 0,
        borderRight: 'none',
    },
});

/* ─── Styled Components ───────────────────────────────────────────────── */
const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
        // Mobile: the drawer never reserves layout width — closed it is hidden,
        // open it overlays (the paper is position:fixed).
        [theme.breakpoints.down('md')]: {
            width: 0,
        },
    }),
);

/* ─── Inline Styles Object ────────────────────────────────────────────── */
const S = {
    appBarGreen: {
        background: 'linear-gradient(135deg, #0A2E1F 0%, #0D3D29 40%, #0F4D33 70%, #1A7A50 100%)',
        boxShadow: '0 4px 24px rgba(10,46,31,0.35)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    appBarAgro: {
        background: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 50%, #4a4a4a 100%)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    logoWrapper: {
        background: 'rgba(255,255,255,0.15)',
        backdropFilter: 'blur(12px)',
        borderRadius: '14px',
        padding: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid rgba(255,255,255,0.25)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
    },
    brandName: {
        color: 'white',
        fontFamily: '"Playfair Display", Georgia, serif',
        marginLeft: 14,
        fontWeight: 700,
        fontSize: '1.35rem',
        letterSpacing: '0.04em',
        textShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },
    brandTag: {
        color: 'rgba(255,255,255,0.65)',
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '0.68rem',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        marginLeft: 14,
        marginTop: -2,
    },
    qrBtn: {
        background: 'rgba(255,255,255,0.12)',
        color: 'white',
        borderRadius: '10px',
        padding: '7px 16px',
        fontSize: '0.8rem',
        fontFamily: '"DM Sans", sans-serif',
        letterSpacing: '0.03em',
        border: '1px solid rgba(255,255,255,0.2)',
        backdropFilter: 'blur(8px)',
        textTransform: 'none',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        '&:hover': { background: 'rgba(255,255,255,0.22)', boxShadow: 'none' },
    },
    inviteBtn: {
        background: 'linear-gradient(135deg, #f0c040 0%, #e8a020 100%)',
        color: '#1a1a1a',
        borderRadius: '10px',
        padding: '7px 16px',
        fontSize: '0.8rem',
        fontFamily: '"DM Sans", sans-serif',
        fontWeight: 600,
        letterSpacing: '0.03em',
        border: 'none',
        textTransform: 'none',
        boxShadow: '0 4px 14px rgba(240,192,64,0.35)',
        transition: 'all 0.2s ease',
    },
    drawerPaperGreen: {
        background: 'linear-gradient(180deg, #081C12 0%, #0A2E1F 35%, #0D3D29 70%, #091A10 100%)',
        borderRight: '1px solid rgba(255,255,255,0.05)',
        boxShadow: '4px 0 32px rgba(10,46,31,0.3)',
    },
    drawerPaperAgro: {
        background: 'linear-gradient(180deg, #1a1a1a 0%, #222 50%, #1f1f1f 100%)',
        borderRight: '1px solid rgba(255,255,255,0.04)',
    },
    sectionDivider: {
        height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
        margin: '8px 16px',
    },
    navItem: {
        borderRadius: '0 40px 40px 0',
        margin: '2px 0 2px 0',
        padding: '8px 12px',
        transition: 'all 0.25s ease',
        position: 'relative',
        overflow: 'hidden',
    },
    navItemActive: {
        background: 'linear-gradient(90deg, rgba(26,122,80,0.35) 0%, rgba(26,122,80,0.08) 100%)',
        borderLeft: '3px solid #1A7A50',
    },
    navIcon: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 18,
        transition: 'color 0.2s',
    },
    navText: {
        color: 'rgba(255,255,255,0.78)',
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '0.88rem',
        letterSpacing: '0.02em',
        marginBottom: 0,
    },
    logoutBtn: {
        color: 'rgba(255,100,100,0.75)',
        borderRadius: '0 40px 40px 0',
        padding: '8px 12px',
        width: '100%',
        transition: 'all 0.2s ease',
        '&:hover': { background: 'rgba(255,80,80,0.1)' },
    },
    drawerBrand: {
        padding: '20px 16px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        marginBottom: 6,
    },
    leafAccent: {
        width: 36,
        height: 36,
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #0F4D33 0%, #1A7A50 50%, #2ECC7A 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(10,46,31,0.3)',
        fontSize: 18,
        color: 'white',
    },
    dialogTitle: {
        background: 'linear-gradient(135deg, #081C12 0%, #0A2E1F 35%, #0D3D29 70%, #091A10 100%)',
        color: 'white',
        fontFamily: '"Playfair Display", serif',
        fontSize: 17,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        padding: '14px 20px',
    },
    dialogContent: {
        background: 'linear-gradient(135deg, #081C12 0%, #0A2E1F 35%, #0D3D29 70%, #091A10 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '520px',
        minHeight: '110px',
        padding: '24px',
    },
    linkBox: {
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(46,204,122,0.3)',
        borderRadius: '12px',
        padding: '14px 20px',
        width: '100%',
        textAlign: 'center',
        color: '#A7F3D0',
        fontFamily: '"DM Mono", monospace',
        fontSize: '0.82rem',
        letterSpacing: '0.02em',
        wordBreak: 'break-all',
    },
};

/* ─── Drawer background texture SVG (inline) ─────────────────────────── */
const DrawerBg = () => (
    <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0.035, pointerEvents: 'none', zIndex: 0 }} xmlns="http://www.w3.org/2000/svg">
        <defs>
            <pattern id="leaf-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                <circle cx="30" cy="30" r="1.5" fill="white" />
                <circle cx="0" cy="0" r="1" fill="white" />
                <circle cx="60" cy="60" r="1" fill="white" />
            </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#leaf-pattern)" />
    </svg>
);

/* ─── Nav Item Component ──────────────────────────────────────────────── */
/* ─── Main Component ──────────────────────────────────────────────────── */
const NavigationBar = ({ unreadMSG, rightActions = null }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    // Initialise from localStorage SYNCHRONOUSLY (lazy initialiser) so the very
    // first render is already correctly scoped. Starting empty and loading these
    // in a useEffect made the sidebar flash EVERY tab on each navigation: an empty
    // permission set makes viewPermission() fall through to its "show everything"
    // default until the async checkAuth returned. checkAuth still runs below to
    // validate/refresh the session — it just no longer drives the first paint.
    const readJSON = (key, fallback) => {
        try {
            const raw = localStorage.getItem(key);
            return raw ? JSON.parse(raw) : fallback;
        } catch (e) {
            return fallback;
        }
    };
    const [userData, setUserData] = useState(() => readJSON('userData', {}));
    const [openCopyDialog, setOpenCopyDialog] = useState(false);
    const [agroDataLocalStorage, setAgroDataLocalStorage] = useState({});
    const [permissions, setPermissions] = useState(() => readJSON('permissions', []));
    // Session could not be re-verified (backend/network down) but is NOT invalid —
    // we keep the user logged in on cached permissions and show a banner.
    const [authDegraded, setAuthDegraded] = useState(false);

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);

    const getUserDataFromLocalStorage = useCallback(() => {
        const userDataFromLocalStorage = localStorage.getItem('userData');
        if (userDataFromLocalStorage) {
            const parsedUserData = JSON.parse(userDataFromLocalStorage);
            setUserData(parsedUserData);
            return parsedUserData;
        } else {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    // checkAuth failure handling (wave-1 item 5): logout ONLY when the backend
    // explicitly says the session is invalid. A network blip or a 5xx used to force
    // a logout — one transient failure logged everyone out. Now transient failures
    // get a bounded retry, then degrade: keep the session, reuse cached permissions,
    // and show the "session unverified" banner until a later check succeeds.
    const AUTH_INVALID_CODES = ['TOKEN_NOT_FOUND', 'USER_NOT_FOUND', 'TOKEN_EXPIRED', 'USER_IS_NOT_AUTHENTICATED'];

    // Wipe the ENTIRE client session and HARD-reload. A plain navigate() leaves
    // Redux + in-memory component state (and any cached localStorage) from the
    // previous account alive, so the next login could see the old account's data.
    // localStorage.clear() + a full page reload guarantees a clean slate. Only the
    // API-host choice (environment) is preserved.
    const hardLogout = () => {
        try {
            const env = localStorage.getItem('environment');
            localStorage.clear();
            if (env) localStorage.setItem('environment', env);
        } catch (e) {
            /* ignore storage errors */
        }
        window.location.replace('/');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const clearSessionAndRedirect = useCallback(() => {
        hardLogout();
    }, []);

    const checkAuthApi = useCallback(async (token) => {
        const MAX_ATTEMPTS = 3; // 1 try + 2 bounded retries on transient failure
        let lastError = null;

        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
                const response = await checkAuth(baseURL, token);
                let data = null;
                try { data = await response.json(); } catch (parseError) { data = null; }

                // Explicit auth-invalid: the backend rejected the SESSION, not the
                // request. validate360Token answers 400 + TOKEN_NOT_FOUND/USER_NOT_FOUND.
                const invalidByCode = AUTH_INVALID_CODES.includes(data?.code) || AUTH_INVALID_CODES.includes(data?.error?.code);
                const invalidByStatus = response.status === 401 || response.status === 403;
                if (invalidByCode || invalidByStatus) {
                    clearSessionAndRedirect();
                    return;
                }

                // Authenticated (permissions may be null for admin/superadmin —
                // backend degrades missing permission docs to 200 + empty).
                if (response.ok && (data?.code === 'USER_IS_AUTHENTICATED' || data?.data)) {
                    const permData = data?.data?.user_permissions ?? data;
                    setPermissions(permData);
                    localStorage.setItem('permissions', JSON.stringify(permData));
                    // Announce it so pages that read permissions from localStorage
                    // (e.g. HomePage) can re-render instead of showing the
                    // "permissions absent => show everything" state until a refresh.
                    window.dispatchEvent(new Event('phy:permissions'));
                    setAuthDegraded(false);
                    return;
                }

                // Anything else (5xx, malformed body) is transient — retry below.
                lastError = new Error(`checkAuth unexpected response (HTTP ${response.status})`);
            } catch (error) {
                lastError = error; // network failure
            }
            if (attempt < MAX_ATTEMPTS) {
                await new Promise((resolve) => setTimeout(resolve, 700 * attempt));
            }
        }

        // Transient failure persisted: DEGRADE, don't log out. Keep the session,
        // fall back to the cached permissions so the sidebar still renders.
        console.error('[NavigationBar] checkAuth unavailable after retries — keeping session (degraded):', lastError?.message);
        setAuthDegraded(true);
        try {
            const cached = localStorage.getItem('permissions');
            if (cached) setPermissions(JSON.parse(cached));
        } catch (cacheError) {
            // cached permissions unreadable — sidebar stays minimal until recovery
        }
    }, [clearSessionAndRedirect]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const userData = getUserDataFromLocalStorage();
        if (userData) {
            checkAuthApi(userData?.access_token);
        }
    }, [checkAuthApi, navigate, getUserDataFromLocalStorage]);

    const handleLogout = async () => {
        // Server logout is best-effort; the client session is ALWAYS cleared and the
        // page hard-reloaded, so no account data can leak into the next login even
        // if the logout API call fails.
        try {
            await logout(baseURL, userData?.id, userData?.email, userData?.access_token);
        } catch (error) {
            console.error('Logout error (clearing session anyway):', error);
        } finally {
            hardLogout();
        }
    };

    const agronomistIdMap = {
        'agro_invite_qr_phyfarm': '65c0d2a9ac84ae512f3c916a',
        'agro_invite_qr_vikas_chauhan': '65dd7d820e400ca5404f014a',
        'agro_invite_qr_kaushik': '65dd7df10e400ca5404f014b',
        'agro_invite_qr_tarun_bhardwaj': '65dd7e900e400ca5404f014c',
        'agro_invite_qr_sushant_h_surve': '65dd7edd0e400ca5404f014d',
        'agro_invite_qr_dankan': '65dd7f1b0e400ca5404f014e',
        'agro_invite_qr_prem_more': '65dd7f450e400ca5404f014f',
        'agro_invite_qr_kirubhakaran_rajendran': '65dd7f7e0e400ca5404f0150',
        'agro_invite_qr_vijay_m': '65dd7fef0e400ca5404f0151',
        'agro_invite_qr_akash_wadkar': '65dd801d0e400ca5404f0152',
        'agro_invite_qr_hussain': '65dd803d0e400ca5404f0153',
        'agro_invite_qr_onelabs': '662f3e64e8e87191dad6db83'
    };

    // id → manage.phyfarm.com slug. The host lives in config (MANAGE_BASE_URL) and the
    // /register-user suffix is added in getInviteLink, so onboarding a new agronomist is
    // a one-line slug add rather than a full hardcoded URL + code deploy.
    const agronomistLinkMap = {
        '65c0d2a9ac84ae512f3c916a': 'phyfarm',
        '65dd7d820e400ca5404f014a': 'vikas-chauhan',
        '65dd7df10e400ca5404f014b': 'kaushik',
        '65dd7e900e400ca5404f014c': 'tarun-bhardwaj',
        '65dd7edd0e400ca5404f014d': 'sushant-h-surve',
        '65dd7f1b0e400ca5404f014e': 'dankan',
        '65dd7f450e400ca5404f014f': 'prem-more',
        '65dd7f7e0e400ca5404f0150': 'kirubhakaran-rajendran',
        '65dd7fef0e400ca5404f0151': 'vijay-m',
        '65dd801d0e400ca5404f0152': 'akash-wadkar',
        '65dd803d0e400ca5404f0153': 'hussain',
        '662f3e64e8e87191dad6db83': 'onelabs'
    };

    const getAgroData = useCallback(() => {
        const agroData = localStorage.getItem('userData');
        if (agroData) setAgroDataLocalStorage(JSON.parse(agroData));
    }, []);

    useEffect(() => { getAgroData(); }, [getAgroData]);

    const getInviteLink = () => {
        const agroID = agroDataLocalStorage?.id;
        const slug = agroID && agronomistLinkMap[agroID];
        return slug ? `${MANAGE_BASE_URL}/${slug}/register-user` : null;
    };

    const downloadImage = async () => {
        const agroID = agroDataLocalStorage?.id;
        if (agroID) {
            const userName = Object.keys(agronomistIdMap).find(key => agronomistIdMap[key] === agroID);
            if (userName) {
                const imageName = userName.toLowerCase().replace(/\s+/g, '-');
                const imageSrc = `/assets/qrs/${imageName}.png`;
                try {
                    const image = await fetch(imageSrc);
                    if (image.ok) {
                        const imageBlob = await image.blob();
                        const imageURL = URL.createObjectURL(imageBlob);
                        const link = document.createElement('a');
                        link.href = imageURL;
                        link.download = `${imageName}.png`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                } catch (error) { console.error('Error fetching image:', error); }
            }
        }
    };

    // Sidebar content is fully manifest-driven now — every feature declares its
    // entry + gating featureName in features/<name>/manifest.jsx, grouped into the
    // 7 capability modules by features/modules.jsx (moduleNavGroups).
    const isAgro = userData?.role?.name === 'agronomist';
    const isSuperAdmin = userData?.role?.name === 'superadmin';

    // A DEALER-type login gets ONE extra, dealer-only tab — their own Leads book —
    // ADDED to the normal permission-driven nav. So a dealer sees the tabs their
    // role grants (People, GPT Chat, …) PLUS this Leads tab. The Leads item is
    // alwaysVisible, so it shows without a leads_mgmt grant; the backend still
    // scopes the data to leads they created and blocks convert. Non-dealers never
    // see it.
    const isDealer = (userData?.role?.name || '').trim().toLowerCase() === 'dealer';
    const dealerLeadsGroup = {
        id: 'dealer-leads',
        title: 'Dealer',
        icon: <LuUserPlus />,
        items: [
            { text: 'Leads', link: '/lead-inbox', icon: <LuUserPlus />, alwaysVisible: true, featureName: 'leads_mgmt' },
            { text: 'Inbox', link: '/inbox', icon: <FaWhatsapp />, alwaysVisible: true, featureName: 'wati_chat_mgmt' },
        ],
    };
    const navGroups = isDealer ? [...moduleNavGroups, dealerLeadsGroup] : moduleNavGroups;

    return (
        <>
            {/* ── Google Fonts (injected once) ─────────────────────── */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&family=DM+Mono&display=swap');

                .phy-nav-item-btn:hover .phy-nav-icon { color: #2ECC7A !important; }
                .phy-nav-item-btn:hover .phy-nav-text { color: white !important; }

                .phy-action-btn {
                    transition: transform 0.18s ease, box-shadow 0.18s ease !important;
                }
                .phy-action-btn:hover { transform: translateY(-1px); }

                 @keyframes slideInLeft {
                     from { opacity: 0; transform: translateX(-12px); }
                     to   { opacity: 1; transform: translateX(0); }
                 }
                 .phy-nav-list-item {
                     animation: slideInLeft 0.28s ease both;
                     margin: 6px 0;
                 }
                 .phy-nav-list-item:nth-child(1)  { animation-delay: 0.04s; }
                 .phy-nav-list-item:nth-child(2)  { animation-delay: 0.08s; }
                 .phy-nav-list-item:nth-child(3)  { animation-delay: 0.12s; }
                .phy-nav-list-item:nth-child(4)  { animation-delay: 0.16s; }
                .phy-nav-list-item:nth-child(5)  { animation-delay: 0.20s; }
                .phy-nav-list-item:nth-child(6)  { animation-delay: 0.24s; }
                .phy-nav-list-item:nth-child(7)  { animation-delay: 0.28s; }
                .phy-nav-list-item:nth-child(8)  { animation-delay: 0.32s; }
                .phy-nav-list-item:nth-child(9)  { animation-delay: 0.36s; }
                .phy-nav-list-item:nth-child(10) { animation-delay: 0.40s; }
            `}</style>

            <Box sx={{ display: 'flex' }}>
                <CssBaseline />

                {/* ══════════════════════════════════════
                    APP BAR
                ══════════════════════════════════════ */}
                <AppBar
                    position="fixed"
                    open={open}
                    style={isAgro ? S.appBarAgro : S.appBarGreen}
                >
                    <Toolbar sx={{ gap: 1 }}>
                        {/* Hamburger */}
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={{
                                marginRight: 1.5,
                                ...(open && { display: 'none' }),
                                width: 44,
                                height: 44,
                                borderRadius: '14px',
                                background: 'rgba(255,255,255,0.10)',
                                border: '1px solid rgba(255,255,255,0.18)',
                                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.20)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    background: 'rgba(255,255,255,0.16)',
                                    borderColor: 'rgba(255,255,255,0.26)',
                                },
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Logo */}
                        <div style={S.logoWrapper}>
                            <img style={{ width: 40, height: 40, borderRadius: 8 }} src={LogoImg} alt="phyfarm" />
                        </div>

                        {/* Brand name + tagline */}
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <span style={S.brandName}>Phyfarm</span>
                            <span style={S.brandTag}>Farm Intelligence Platform</span>
                        </div>

                        {/* Spacer */}
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                            {rightActions}

                            {isAgro && (
                                <>
                                    <Button
                                        variant="contained"
                                        onClick={downloadImage}
                                        className="phy-action-btn"
                                        sx={{
                                            background: 'rgba(255,255,255,0.13)',
                                            color: 'white',
                                            borderRadius: '10px',
                                            padding: '7px 16px',
                                            fontSize: '0.8rem',
                                            fontFamily: '"DM Sans", sans-serif',
                                            letterSpacing: '0.03em',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            textTransform: 'none',
                                            boxShadow: 'none',
                                            '&:hover': { background: 'rgba(255,255,255,0.22)', boxShadow: 'none' },
                                        }}
                                    >
                                        <BsQrCode style={{ marginRight: 8 }} />
                                        Download QR
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={() => setOpenCopyDialog(true)}
                                        className="phy-action-btn"
                                        sx={{
                                            background: 'linear-gradient(135deg, #f0c040 0%, #e8a020 100%)',
                                            color: '#1a1a1a',
                                            borderRadius: '10px',
                                            padding: '7px 16px',
                                            fontSize: '0.8rem',
                                            fontFamily: '"DM Sans", sans-serif',
                                            fontWeight: 600,
                                            letterSpacing: '0.03em',
                                            textTransform: 'none',
                                            boxShadow: '0 4px 14px rgba(240,192,64,0.4)',
                                            '&:hover': { boxShadow: '0 6px 20px rgba(240,192,64,0.55)' },
                                        }}
                                    >
                                        <LuCopy style={{ marginRight: 8 }} />
                                        Invite Link
                                    </Button>
                                </>
                            )}
                        </div>
                    </Toolbar>
                    {authDegraded && (
                        <Box
                            role="status"
                            sx={{
                                bgcolor: 'warning.main',
                                color: '#1a1a1a',
                                px: 2,
                                py: 0.5,
                                fontSize: '0.8rem',
                                fontFamily: '"DM Sans", sans-serif',
                                fontWeight: 600,
                                textAlign: 'center',
                            }}
                        >
                            Couldn't verify your session — the server is unreachable. You're still signed in; some
                            data may be out of date.
                        </Box>
                    )}
                </AppBar>

                {/* ══════════════════════════════════════
                    DRAWER
                ══════════════════════════════════════ */}
                <Drawer
                    variant="permanent"
                    open={open}
                    sx={{
                        '& .MuiDrawer-paper': isAgro ? S.drawerPaperAgro : S.drawerPaperGreen,
                    }}
                >
                    {/* Background texture */}
                    <DrawerBg />

                    {/* Drawer close header */}
                    <DrawerHeader
                        sx={{
                            background: 'transparent',
                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <IconButton
                            onClick={handleDrawerClose}
                            sx={{
                                color: 'rgba(255,255,255,0.4)',
                                borderRadius: '10px',
                                '&:hover': { color: 'white', background: 'rgba(255,255,255,0.08)' },
                            }}
                        >
                            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </DrawerHeader>

                    {/* ── Drawer brand strip (visible when open) ──── */}
                    {open && (
                        <div style={{
                            padding: '16px 20px 14px',
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                            position: 'relative', zIndex: 1,
                            background: 'rgba(255,255,255,0.02)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={S.logoWrapper}>
                                    <img style={{ width: 34, height: 34, borderRadius: 8, display: 'block' }} src={LogoImg} alt="phyfarm" />
                                </div>
                                <div>
                                    <div style={{ color: 'white', fontFamily: '"Playfair Display", serif', fontWeight: 700, fontSize: '1rem' }}>
                                        Phyfarm
                                    </div>
                                    <div style={{ color: 'rgba(167,243,208,0.7)', fontFamily: '"DM Sans", sans-serif', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                                        {(userData?.role?.name || (isSuperAdmin ? 'Super Admin' : 'Admin'))} Portal
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Nav Items ────────────────────────────────── */}
                    <div style={{ position: 'relative', zIndex: 1, flex: 1, overflowY: 'auto', paddingTop: 8 }}>

                        {/* MODULE NAV — the 7 capability modules (features/modules.jsx),
                            for EVERY user (RBAC_MODEL: the menu is DATA, never a
                            hardcoded per-role branch). A module heading renders when ANY
                            of its child features is granted; null permissions
                            (admin/super-admin full access) show every module. The old
                            hardcoded agronomist nav is gone — an agronomist now sees
                            exactly the features their role grants. */}
                        {(
                            <List sx={{ pt: 1 }}>
                                {navGroups.map((module) => {
                                    // `alwaysVisible` exists for the landing page: everyone is
                                    // sent to /home after login, so hiding it from a scoped
                                    // user's nav would strand them with no way back to it.
                                    const grantedItems = module.items.filter((item) =>
                                        item.alwaysVisible || viewPermission(item.featureName, permissions));
                                    if (grantedItems.length === 0) return null;
                                    return (
                                        <Box key={module.id} sx={{ mb: 0.75 }}>
                                            {open && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        px: 2, pt: 1, pb: 0.25,
                                                        color: 'rgba(255,255,255,0.38)',
                                                        fontFamily: '"DM Sans", sans-serif',
                                                        fontSize: '0.62rem',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.14em',
                                                        textTransform: 'uppercase',
                                                    }}
                                                >
                                                    {module.title}
                                                </Typography>
                                            )}
                                            {grantedItems.map((item) => (
                                                <ListItem key={`${module.id}-${item.link}`} disablePadding className="phy-nav-list-item">
                                                    <ListItemButton
                                                        className="phy-nav-item-btn"
                                                        onClick={() => navigate(item.link, { replace: true })}
                                                        sx={{
                                                            borderRadius: '0 40px 40px 0',
                                                            mr: 2, py: 1.1, px: 1.5,
                                                            transition: 'all 0.22s ease',
                                                            ...(location.pathname === item.link && S.navItemActive),
                                                            '&:hover': { background: 'linear-gradient(90deg, rgba(26,122,80,0.22) 0%, rgba(26,122,80,0.04) 100%)' },
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            className="phy-nav-icon"
                                                            sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18, minWidth: 0, mr: open ? 1.8 : 'auto', transition: 'color 0.2s', display: 'flex', alignItems: 'center' }}
                                                        >
                                                            {item.icon}
                                                        </ListItemIcon>
                                                        <Link
                                                            to={item.link}
                                                            className="phy-nav-text"
                                                            style={{
                                                                color: 'rgba(255,255,255,0.72)',
                                                                textDecoration: 'none',
                                                                fontFamily: '"DM Sans", sans-serif',
                                                                fontSize: '0.87rem',
                                                                opacity: open ? 1 : 0,
                                                                transition: 'opacity 0.2s, color 0.2s',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {item.text}
                                                        </Link>
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </Box>
                                    );
                                })}
                            </List>
                        )}
                    </div>

                    {/* ── Logout ───────────────────────────────────── */}
                    <div style={{
                        position: 'relative', zIndex: 1,
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                        padding: '8px 0 12px',
                    }}>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={handleLogout}
                                sx={{
                                    borderRadius: '0 40px 40px 0',
                                    mr: 2, py: 1.1, px: 1.5,
                                    transition: 'all 0.22s ease',
                                    '&:hover': {
                                        background: 'rgba(255,80,80,0.1)',
                                        '& .logout-icon': { color: '#ff6464 !important' },
                                        '& .logout-text': { color: '#ff6464 !important' },
                                    },
                                }}
                            >
                                <ListItemIcon
                                    className="logout-icon"
                                    sx={{ color: 'rgba(255,160,160,0.6)', minWidth: 0, mr: open ? 1.8 : 'auto', transition: 'color 0.2s', fontSize: 18 }}
                                >
                                    <RiLogoutCircleLine style={{ fontSize: 18 }} />
                                </ListItemIcon>
                                <ListItemText
                                    className="logout-text"
                                    primary="Logout"
                                    primaryTypographyProps={{
                                        sx: {
                                            fontFamily: '"DM Sans", sans-serif',
                                            fontSize: '0.87rem',
                                            color: 'rgba(255,160,160,0.7)',
                                            transition: 'color 0.2s',
                                        }
                                    }}
                                    sx={{ opacity: open ? 1 : 0 }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </div>
                </Drawer>

                {/* ══════════════════════════════════════
                    INVITE LINK DIALOG
                ══════════════════════════════════════ */}
                <Dialog
                    open={openCopyDialog}
                    onClose={() => setOpenCopyDialog(false)}
                    PaperProps={{
                        sx: {
                            borderRadius: '18px',
                            overflow: 'hidden',
                            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                            border: '1px solid rgba(26,122,80,0.2)',
                        }
                    }}
                >
                    <DialogTitle style={S.dialogTitle}>
                        <span>
                            🔗&nbsp;&nbsp;Invite Link
                        </span>
                        <IconButton
                            onClick={() => setOpenCopyDialog(false)}
                            sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white', background: 'rgba(255,255,255,0.1)' } }}
                        >
                            <IoCloseOutline />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent style={S.dialogContent}>
                        <div style={S.linkBox}>
                            {getInviteLink() || "Invite link not available for this account."}
                        </div>
                    </DialogContent>
                </Dialog>
            </Box>
        </>
    );
};

export default NavigationBar;
